import React, { useState } from 'react';
import { Phone, MapPin, Clock, Star, Shield, MessageCircle, ChevronDown, ChevronUp, Heart } from 'lucide-react';
import { useFavoriteTaxiStands } from '../hooks/useFavoriteTaxiStands';
import TaxiStandReviews from './TaxiStandReviews';

interface TaxiStandCardProps {
  stand: {
    id: string;
    name: string;
    address: string;
    phone: string;
    rating: number;
    available_taxis: number;
    total_taxis: number;
    working_hours: string;
    is_verified: boolean;
    total_reviews: number;
  };
}

export default function TaxiStandCard({ stand }: TaxiStandCardProps) {
  const [showReviews, setShowReviews] = useState(false);
  const { addFavorite, removeFavorite, isFavorite } = useFavoriteTaxiStands();

  const handleFavoriteToggle = () => {
    if (isFavorite(stand.id)) {
      removeFavorite(stand.id);
    } else {
      addFavorite(stand);
    }
  };

  return (
    <div className="bg-gray-800/90 backdrop-blur-sm rounded-xl overflow-hidden border border-white/5 hover:border-yellow-400/20 transition-all duration-300">
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-lg flex items-center justify-center">
              <span className="text-xl font-black text-gray-900">T</span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-white">{stand.name}</h2>
                {stand.is_verified && (
                  <div className="bg-yellow-400/10 text-yellow-400 px-2 py-0.5 rounded-full flex items-center gap-1 text-xs font-medium">
                    <Shield className="w-3 h-3" />
                    Onaylı
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex items-center gap-1">
                  <Star className="w-3.5 h-3.5 text-yellow-400 fill-current" />
                  <span className="text-white text-sm font-medium">{stand.rating.toFixed(1)}</span>
                  <span className="text-gray-400 text-xs">({stand.total_reviews})</span>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={handleFavoriteToggle}
            className="p-1.5 rounded-lg bg-gray-700/50 hover:bg-gray-700 transition-colors"
          >
            <Heart 
              className={`w-4 h-4 ${
                isFavorite(stand.id) 
                  ? 'text-red-400 fill-red-400' 
                  : 'text-gray-400 hover:text-red-400'
              }`}
            />
          </button>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-1 gap-2 my-4 text-sm">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-yellow-400 flex-shrink-0" />
            <p className="text-gray-300">{stand.address}</p>
          </div>
          
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-yellow-400 flex-shrink-0" />
            <p className="text-gray-300">{stand.phone}</p>
          </div>
          
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-yellow-400 flex-shrink-0" />
            <p className="text-gray-300">Çalışma Saatleri: {stand.working_hours}</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-2">
          <a
            href={`tel:${stand.phone}`}
            className="flex-1 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-gray-900 py-2 px-4 rounded-lg font-medium transition-all active:scale-[0.98] flex items-center justify-center gap-2 text-sm"
          >
            <Phone className="w-4 h-4" />
            <span>Taksi Çağır</span>
          </a>
          <button 
            onClick={() => setShowReviews(!showReviews)}
            className="flex-1 bg-gray-700/50 hover:bg-gray-700 text-white py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 text-sm"
          >
            <MessageCircle className="w-4 h-4" />
            <span>Yorumlar</span>
            {showReviews ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Reviews Section */}
      {showReviews && (
        <div className="border-t border-gray-700/50 mt-4 p-4">
          <TaxiStandReviews
            taxiStandId={stand.id}
            totalReviews={stand.total_reviews}
            rating={stand.rating}
          />
        </div>
      )}
    </div>
  );
}